import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { getAuthHeader } from '../customAuth/authContext';
import { useAuth } from '../customAuth/authStateHook';
import { backgroundActionsUrl } from '../config';

interface BackgroundJobsGuardProps {
    children: React.ReactNode;
}

type GuardState = 'loading' | 'authorized' | 'unauthorized' | 'error';

const BackgroundJobsGuard: React.FC<BackgroundJobsGuardProps> = ({ children }) => {
    const { isSuperAdmin, canAccessInternalTools } = useAuth();
    const [guardState, setGuardState] = useState<GuardState>('loading');

    useEffect(() => {
        const checkAccess = async () => {
            // Dev tools (logs, background jobs) require internal-tools access, not
            // just super-admin. Hiding the nav entry is not access control on its
            // own — the route must reject direct navigation too.
            if (!isSuperAdmin || !canAccessInternalTools) {
                setGuardState('unauthorized');
                return;
            }

            try {
                const response = await fetch(`${backgroundActionsUrl}/api/background-jobs`, {
                    method: 'HEAD',
                    headers: {
                        'Authorization': getAuthHeader(),
                    }
                });

                if (response.ok) {
                    setGuardState('authorized');
                } else if (response.status === 403) {
                    setGuardState('unauthorized');
                } else if (response.status === 401) {
                    setGuardState('unauthorized');
                } else {
                    setGuardState('error');
                }

            } catch (error) {
                console.error('Access check failed:', error);
                setGuardState('error');
            }
        };

        checkAccess();
    }, [isSuperAdmin, canAccessInternalTools]);

    if (guardState === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (guardState === 'unauthorized' || guardState === 'error') {
        return <Navigate to="/access-denied" replace />;
    }

    return <>{children}</>;
};

export default BackgroundJobsGuard;